import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Check,
  Globe,
  Loader2,
  Lock,
  Shield,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button, Input, Textarea } from '../../../../components/ui';
import { trpc } from '../../../../lib/trpc';

export const Route = createFileRoute('/app/clubs/join/$code')({
  component: JoinByCodePage,
});

function JoinByCodePage() {
  const { code } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: preview, isLoading, error: previewError } = trpc.clubInvite.previewLink.useQuery({ code });
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();

  const isAlreadyMember = preview && myClubs?.some((c) => c.id === preview.id);

  const joinMutation = trpc.clubMember.join.useMutation({
    onSuccess: (data) => {
      toast.success('Joined club!');
      utils.club.getMyClubs.invalidate();
      if ('slug' in data && data.slug) {
        navigate({ to: '/app/clubs/$slug', params: { slug: data.slug } });
      } else {
        navigate({ to: '/app/clubs' });
      }
    },
  });

  const joinIsPending = joinMutation.isPending;
  const joinError = joinMutation.isError ? joinMutation.error : null;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Loader2 className="size-10 text-brand-400 animate-spin mb-4" />
        <h2 className="text-xl font-display font-bold text-white mb-1">Loading invite...</h2>
        <p className="text-sm text-surface-400">Resolving your invite link</p>
      </div>
    );
  }

  if (previewError || !preview) {
    const err = previewError;
    let message = 'This invite link is invalid or has been revoked';
    if (err?.data?.code === 'BAD_REQUEST') message = err.message;

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="relative mb-5">
          <div className="absolute inset-0 rounded-full bg-red-500/15 blur-2xl scale-150" />
          <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl bg-surface-800/80 border border-surface-700/50">
            <XCircle className="size-8 text-red-400" />
          </div>
        </div>
        <h2 className="text-xl font-display font-bold text-white mb-2">Invalid Invite</h2>
        <p className="text-sm text-surface-400 max-w-xs mb-6">{message}</p>
        <Button variant="outline" onClick={() => navigate({ to: '/app/clubs' })}>
          Browse Clubs
        </Button>
      </div>
    );
  }

  const privacyConfig = {
    public: { icon: Globe, label: 'Public', color: 'text-green-400' },
    invite_only: { icon: Shield, label: 'Invite Only', color: 'text-purple-400' },
    private: { icon: Lock, label: 'Private', color: 'text-yellow-400' },
  };

  const privacy = privacyConfig[preview.privacy];
  const PrivacyIcon = privacy.icon;
  const isPrivate = preview.privacy === 'private';

  const joinErrorMessage = joinError
    ? joinError.data?.code === 'CONFLICT'
      ? 'You are already a member of this club'
      : joinError.data?.code === 'FORBIDDEN'
        ? joinError.message
        : 'Failed to join club'
    : null;

  return (
    <div className="max-w-md mx-auto p-4 md:p-6 mt-8">
      <div className="rounded-2xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
        {/* Banner */}
        <div className="relative h-32">
          {preview.bannerUrl ? (
            <img src={preview.bannerUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-900/60 via-surface-800 to-surface-950" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-surface-900 via-surface-900/40 to-transparent" />
        </div>

        {/* Club info */}
        <div className="px-6 -mt-8 relative">
          <div className="flex items-end gap-4">
            {preview.avatarUrl ? (
              <img
                src={preview.avatarUrl}
                alt={preview.name}
                className="w-16 h-16 rounded-2xl bg-surface-700 object-cover ring-4 ring-surface-900 shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-surface-700 to-surface-800 flex items-center justify-center text-white font-display font-bold text-2xl ring-4 ring-surface-900 shrink-0">
                {preview.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="pb-1 min-w-0 flex-1">
              <h1 className="text-xl font-display font-bold text-white truncate">{preview.name}</h1>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {preview.description && (
            <p className="text-sm text-surface-400 line-clamp-3">{preview.description}</p>
          )}

          <div className="flex items-center gap-4 text-sm text-surface-400">
            <div className="flex items-center gap-1.5">
              <Users className="size-4 text-surface-500" />
              <span className="font-semibold tabular-nums text-surface-200">{preview.memberCount}</span>
              <span>members</span>
            </div>
            <div className="flex items-center gap-1.5">
              <PrivacyIcon className={`size-4 ${privacy.color}`} />
              <span>{privacy.label}</span>
            </div>
          </div>

          {joinErrorMessage && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <XCircle className="size-4 text-red-400 shrink-0" />
              <p className="text-sm text-red-400">{joinErrorMessage}</p>
            </div>
          )}

          {isAlreadyMember ? (
            <Button
              variant="primary"
              className="w-full"
              leftIcon={<Check className="size-4" />}
              onClick={() => navigate({ to: '/app/clubs/$slug', params: { slug: preview.slug } })}
            >
              Go to Club
            </Button>
          ) : isPrivate ? (
            <ApplicationForm clubId={preview.id} clubName={preview.name} />
          ) : (
            <Button
              variant="primary"
              className="w-full"
              leftIcon={
                joinIsPending
                  ? <Loader2 className="size-4 animate-spin" />
                  : <UserPlus className="size-4" />
              }
              disabled={joinIsPending}
              onClick={() => joinMutation.mutate({ linkCode: code })}
            >
              {joinIsPending ? 'Joining...' : 'Join Club'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ApplicationForm({ clubId, clubName }: { clubId: string; clubName: string }) {
  const navigate = useNavigate();
  const [answer, setAnswer] = useState('');

  const submitMutation = trpc.clubInvite.submitApplication.useMutation({
    onSuccess: () => {
      toast.success('Application submitted! You\'ll be notified when it\'s reviewed.');
      navigate({ to: '/app/clubs' });
    },
    onError: (err) => {
      if (err.data?.code === 'CONFLICT') {
        toast.error('You already have a pending application for this club');
      } else {
        toast.error(err.message || 'Failed to submit application');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMutation.mutate({
      clubId,
      answers: answer.trim() ? [answer.trim()] : [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
        <p className="text-xs text-yellow-400 font-medium">
          This club requires an application to join
        </p>
      </div>
      <Textarea
        label="Why do you want to join?"
        placeholder="Tell the admins a bit about yourself..."
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={3}
        maxLength={500}
      />
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        disabled={submitMutation.isPending}
        leftIcon={submitMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
      >
        {submitMutation.isPending ? 'Submitting...' : 'Submit Application'}
      </Button>
    </form>
  );
}
